import axios from "./AxiosClient";
import { Constants } from "@socialcoach/services";
import {
  ExtraFieldsParam,
  LiquidDrop,
  Page,
  PlayerCard,
  PlayerCardMetric,
  PlayerCardMetricSummaries,
  PlayerCardTemplate,
  PlayerCardTemplateDefault,
  PlayerCardTheme,
  PlayerCardThemeDefault,
} from "../../services/bedrock";
import { scCatchServiceException } from "../../annotations";
import { DeepPartial } from "../../utils";
import { CancelToken } from "axios";

const PLAYER_CARDS_SERVICE = `${Constants.API_URL}/bedrock/player_cards`;
const PLAYER_CARD_THEMES_SERVICE = `${Constants.API_URL}/bedrock/player_card_themes`;
const PLAYER_CARD_THEME_DEFAULTS_SERVICE = `${Constants.API_URL}/bedrock/player_card_theme_defaults`;
const PLAYER_CARD_TEMPLATES_SERVICE = `${Constants.API_URL}/bedrock/player_card_templates`;
const PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE = `${Constants.API_URL}/bedrock/player_card_template_defaults`;
const PLAYER_CARD_METRICS_SERVICE = `${Constants.API_URL}/bedrock/player_card_metrics`;

export type PlayerCardSortColumn = "player" | "template" | "theme" | "views" | "clicks";
export interface PlayerCardCreateRequestBody {
  playerIds?: number[];
  themeId?: number;
  templateId?: number;
  sendNotification?: boolean;
  grantPlayerCardAccess?: boolean;
  grantPlayerCardAccessGlobally?: boolean;
}
export interface PlayerCardUpdateRequestBody
  extends Omit<PlayerCard, "id" | "url" | "playerId" | "player" | "theme" | "template"> {
  grantPlayerCardAccess?: boolean;
  sendNotification?: boolean;
}
export interface PlayerCardThemeRequestBody extends Omit<PlayerCardTheme, "id" | "coachId" | "playerId"> {}
export interface PlayerCardTemplateRequestBody extends Omit<PlayerCardTemplate, "id" | "coachId"> {}
export interface PlayerCardFilters {
  playerId?: number | string | number[];
  themeId?: number | number[];
  templateId?: number | number[];
  branchId?: number | number[];
  regionId?: number | number[];
}
export interface MetricSummariesFilters {
  playerCardWidgetId?: number;
  metricType?: "VIEW" | "CLICK";
  startDate?: Date;
  endDate?: Date;
}
export interface MetricFilters extends MetricSummariesFilters {
  page?: number;
  perPage?: number;
  sort?: string;
  order?: "asc" | "desc";
}

export class PlayerCardServices {
  // ================= PLAYER CARD ENDPOINTS =================
  @scCatchServiceException()
  static async createPlayerCard(requestBody: PlayerCardCreateRequestBody): Promise<PlayerCard[]> {
    const url = PLAYER_CARDS_SERVICE;
    const { data } = await axios.post<PlayerCard[]>(url, requestBody);
    return data;
  }

  @scCatchServiceException()
  static async getPlayerCards(
    page = 1,
    perPage = 15,
    filters?: PlayerCardFilters,
    sort?: PlayerCardSortColumn,
    order?: "asc" | "desc"
  ): Promise<Page<PlayerCard>> {
    const url = PLAYER_CARDS_SERVICE;
    const extraFields: ExtraFieldsParam = {
      player: ["regionNames", "branchNames", "allowPlayerAccessToPlayerCardsPermission"],
    };
    const { data } = await axios.get<Page<PlayerCard>>(url, {
      params: { ...filters, page, perPage, sort, order, extraFields },
    });
    return data;
  }

  @scCatchServiceException()
  static async getMainPlayerCard(playerId?: number | string): Promise<PlayerCard | null> {
    const { content: playerCards } = await this.getPlayerCards(1, 1, { playerId });
    return playerCards[0] || null;
  }

  @scCatchServiceException()
  static async getPlayerCard(id: number): Promise<PlayerCard> {
    const url = `${PLAYER_CARDS_SERVICE}/${id}`;
    const { data } = await axios.get<PlayerCard>(url);
    return data;
  }

  @scCatchServiceException()
  static async updatePlayerCard(
    playerCardId: number,
    requestBody: DeepPartial<PlayerCardUpdateRequestBody>
  ): Promise<PlayerCard> {
    const url = `${PLAYER_CARDS_SERVICE}/${playerCardId}`;
    const { data } = await axios.patch(url, requestBody);
    return data;
  }

  @scCatchServiceException()
  static async deletePlayerCard(playerCardId: number): Promise<void> {
    const url = `${PLAYER_CARDS_SERVICE}/${playerCardId}`;
    await axios.delete(url);
  }

  @scCatchServiceException()
  @scCatchServiceException()
  static async getLiquidVariables(id?: number): Promise<LiquidDrop> {
    var url = PLAYER_CARDS_SERVICE;
    if (id) url = `${url}/${id}`;
    url = `${url}/liquid_variables`;
    const { data } = await axios.get(url);
    return data;
  }

  // ================= PLAYER CARD THEME ENDPOINTS =================
  @scCatchServiceException()
  static async createTheme(theme: PlayerCardThemeRequestBody): Promise<PlayerCardTheme> {
    const url = PLAYER_CARD_THEMES_SERVICE;
    const { data } = await axios.post<PlayerCardTheme>(url, theme);
    return data;
  }

  @scCatchServiceException()
  static async getThemes(
    search?: string,
    playerId?: number,
    page = 1,
    perPage = 25,
    cancelToken?: CancelToken
  ): Promise<Page<PlayerCardTheme>> {
    const url = PLAYER_CARD_THEMES_SERVICE;
    const { data } = await axios.get<Page<PlayerCardTheme>>(url, {
      params: { search, playerId, page, perPage },
      cancelToken,
    });
    return data;
  }

  @scCatchServiceException()
  static async updateTheme(themeId: number, theme: Partial<PlayerCardThemeRequestBody>): Promise<PlayerCardTheme> {
    const url = `${PLAYER_CARD_THEMES_SERVICE}/${themeId}`;
    const { data } = await axios.patch<PlayerCardTheme>(url, theme);
    return data;
  }

  @scCatchServiceException()
  static async deleteTheme(themeId: number): Promise<void> {
    const url = `${PLAYER_CARD_THEMES_SERVICE}/${themeId}`;
    await axios.delete<void>(url);
  }

  // ================= PLAYER CARD THEME DEFAULT ENDPOINTS =================
  @scCatchServiceException()
  static async createThemeDefault(themeId: number, segmentationDetailId?: number): Promise<PlayerCardThemeDefault> {
    const url = PLAYER_CARD_THEME_DEFAULTS_SERVICE;
    const body = {
      segmentationDetailId,
      themeId,
    };
    const { data } = await axios.post<PlayerCardThemeDefault>(url, body);
    data.defaultId = data.themeId;
    return data;
  }

  @scCatchServiceException()
  static async getThemeDefaults(page = 1, perPage = 25): Promise<Page<PlayerCardThemeDefault>> {
    const url = PLAYER_CARD_THEME_DEFAULTS_SERVICE;
    const { data } = await axios.get<Page<PlayerCardThemeDefault>>(url, {
      params: { page, perPage },
    });
    data.content = data.content.map(themeDefault => {
      return {
        ...themeDefault,
        defaultId: themeDefault.themeId,
      };
    });
    return data;
  }

  @scCatchServiceException()
  static async getThemeDefault(themeDefaultId: number): Promise<PlayerCardThemeDefault> {
    const url = `${PLAYER_CARD_THEME_DEFAULTS_SERVICE}/${themeDefaultId}`;
    const { data } = await axios.get<PlayerCardThemeDefault>(url);
    data.defaultId = data.themeId;
    return data;
  }

  @scCatchServiceException()
  static async updateThemeDefault(
    themeDefaultId: number,
    themeId: number | null,
    segmentationDetailId?: number
  ): Promise<PlayerCardThemeDefault> {
    const url = `${PLAYER_CARD_THEME_DEFAULTS_SERVICE}/${themeDefaultId}`;
    const body = {
      segmentationDetailId,
      themeId,
    };
    const { data } = await axios.patch<PlayerCardThemeDefault>(url, body);
    data.defaultId = data.themeId;
    return data;
  }

  @scCatchServiceException()
  static async deleteThemeDefault(themeDefaultId: number): Promise<void> {
    const url = `${PLAYER_CARD_THEME_DEFAULTS_SERVICE}/${themeDefaultId}`;
    await axios.delete<void>(url);
  }

  // ================= PLAYER CARD TEMPLATE ENDPOINTS =================
  @scCatchServiceException()
  static async createTemplate(name: string, templateId?: number): Promise<PlayerCardTemplate> {
    const url = PLAYER_CARD_TEMPLATES_SERVICE;
    const { data } = await axios.post<PlayerCardTemplate>(url, { name, templateId });
    return data;
  }

  @scCatchServiceException()
  static async getTemplates(
    search?: string,
    page: number = 1,
    perPage: number = 25,
    cancelToken?: CancelToken
  ): Promise<Page<PlayerCardTemplate>> {
    const url = PLAYER_CARD_TEMPLATES_SERVICE;
    const { data } = await axios.get<Page<PlayerCardTemplate>>(url, {
      params: { search, page, perPage },
      cancelToken,
    });
    return data;
  }

  @scCatchServiceException()
  static async getTemplate(templateId: number): Promise<PlayerCardTemplate> {
    const url = `${PLAYER_CARD_TEMPLATES_SERVICE}/${templateId}`;
    const { data } = await axios.get<PlayerCardTemplate>(url);
    return data;
  }

  @scCatchServiceException()
  static async updateTemplate(
    templateId: number,
    template: DeepPartial<PlayerCardTemplateRequestBody>
  ): Promise<PlayerCardTemplate> {
    const url = `${PLAYER_CARD_TEMPLATES_SERVICE}/${templateId}`;
    const { data } = await axios.patch<PlayerCardTemplate>(url, template);
    return data;
  }

  @scCatchServiceException()
  static async deleteTemplate(templateId: number): Promise<void> {
    const url = `${PLAYER_CARD_TEMPLATES_SERVICE}/${templateId}`;
    await axios.delete<void>(url);
  }

  // ================= PLAYER CARD TEMPLATE DEFAULT ENDPOINTS =================
  @scCatchServiceException()
  static async createTemplateDefault(
    templateId: number,
    segmentationDetailId?: number
  ): Promise<PlayerCardTemplateDefault> {
    const url = PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE;
    const body = {
      templateId,
      segmentationDetailId,
    };
    const { data } = await axios.post<PlayerCardTemplateDefault>(url, body);
    data.defaultId = data.templateId;
    return data;
  }

  @scCatchServiceException()
  static async getTemplateDefaults(page = 1, perPage = 25): Promise<Page<PlayerCardTemplateDefault>> {
    const url = PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE;
    const { data } = await axios.get<Page<PlayerCardTemplateDefault>>(url, {
      params: { page, perPage },
    });
    data.content = data.content.map(templateDefault => {
      return {
        ...templateDefault,
        defaultId: templateDefault.templateId,
      };
    });
    return data;
  }

  @scCatchServiceException()
  static async getTemplateDefault(templateDefaultId: number): Promise<PlayerCardTemplateDefault> {
    const url = `${PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE}/${templateDefaultId}`;
    const { data } = await axios.get<PlayerCardTemplateDefault>(url);
    data.defaultId = data.templateId;
    return data;
  }

  @scCatchServiceException()
  static async updateTemplateDefault(
    templateDefaultId: number,
    templateId: number | null,
    segmentationDetailId?: number
  ): Promise<PlayerCardTemplateDefault> {
    const url = `${PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE}/${templateDefaultId}`;
    const body = {
      segmentationDetailId,
      templateId,
    };
    const { data } = await axios.patch<PlayerCardTemplateDefault>(url, body);
    data.defaultId = data.templateId;
    return data;
  }

  @scCatchServiceException()
  static async deleteTemplateDefault(templateDefaultId: number): Promise<void> {
    const url = `${PLAYER_CARD_TEMPLATE_DEFAULTS_SERVICE}/${templateDefaultId}`;
    await axios.delete<void>(url);
  }

  // ================= METRIC ENDPOINTS =================
  @scCatchServiceException()
  static async getMetrics(
    playerCardId: number | undefined,
    filters: MetricFilters = {}
  ): Promise<Page<PlayerCardMetric>> {
    const url = PLAYER_CARD_METRICS_SERVICE;
    filters.page = filters.page ?? 1;
    filters.perPage = filters.perPage ?? 15;
    filters.sort = filters.sort ?? "created_date";
    filters.order = filters.order ?? "desc";
    const { data } = await axios.get<Page<PlayerCardMetric>>(url, {
      params: { playerCardId, ...filters },
    });
    return data;
  }

  @scCatchServiceException()
  static async getMetricSummaries(
    playerCardId: number | undefined,
    filters: MetricSummariesFilters = {}
  ): Promise<PlayerCardMetricSummaries> {
    const url = `${PLAYER_CARD_METRICS_SERVICE}/summaries`;
    const { data } = await axios.get<PlayerCardMetricSummaries>(url, {
      params: { playerCardId, ...filters },
    });
    return data;
  }

  // ================= HELPER ENDPOINTS =================
  @scCatchServiceException()
  static async fetchUrlMetadata(url: string): Promise<any> {
    const { data } = await axios.post(`${Constants.API_URL}/bedrock/metadata/fetch`, { url });
    return data;
  }
}
